import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axiosInstance from "../../utils/axiosConfig";
import { setCheckinToken } from "../utils/checkinToken";

// STI-3024: check-in messages carry a short <ui>/c/<code> link. The code is exchanged for the
// appointment to open (the same patient's next booked one if it was rescheduled or rebooked) and a
// token for it. The token is kept for the tab and never goes into the address bar.

const RESOLVE_URL = "/api/athena/digital-checkin/c/";

export const SHORT_LINK_MESSAGES = {
  notFound:
    "This check-in link isn't valid. Please use the link from your most recent appointment message, or call the clinic.",
  noAppointment:
    "We couldn't find an upcoming appointment for this link. If you have rescheduled, please use the link from your most recent appointment message, or call the clinic.",
  tooMany: "Too many attempts. Please try again later, or call the clinic.",
  unavailable: "Check-in isn't available right now. Please try again later, or call the clinic.",
  generic: "Something went wrong. Please try again, or call the clinic.",
};

const CheckinShortLink: React.FC = () => {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!code) {
      setError(SHORT_LINK_MESSAGES.notFound);
      return;
    }
    let cancelled = false;
    axiosInstance
      .get(RESOLVE_URL + encodeURIComponent(code), {
        headers: { client: "axis", domain: "axis" },
        skipGlobalError: true,
      } as any)
      .then((response) => {
        if (cancelled) return;
        const appointmentId = response.data?.appointmentId;
        const token = response.data?.token;
        if (typeof token !== "string" || !token || appointmentId === undefined || appointmentId === null) {
          setError(SHORT_LINK_MESSAGES.generic);
          return;
        }
        setCheckinToken(token);
        navigate(`/start-checkin/${encodeURIComponent(String(appointmentId))}`, { replace: true });
      })
      .catch((err: any) => {
        if (cancelled) return;
        const status = err?.response?.status;
        if (status === 404) {
          setError(SHORT_LINK_MESSAGES.notFound);
        } else if (status === 410) {
          setError(SHORT_LINK_MESSAGES.noAppointment);
        } else if (status === 429) {
          setError(SHORT_LINK_MESSAGES.tooMany);
        } else if (status === 503) {
          setError(SHORT_LINK_MESSAGES.unavailable);
        } else {
          setError(SHORT_LINK_MESSAGES.generic);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [code, navigate]);

  return (
    <div className="min-h-screen bg-[#F7F8FD] flex flex-col">
      <div className="flex flex-row px-10 py-6 shadow justify-between bg-white items-center">
        <span className="text-[#01545D] font-semibold text-2xl">
          Axis Integrated Mental Health
        </span>
        <span className="text-[#01545D] font-medium text-xl">Patient Check-In</span>
      </div>
      <main className="flex-grow container mx-auto flex flex-col items-center justify-center px-4 py-10">
        {error ? (
          <div className="flex flex-col items-center max-w-xl text-center">
            <div className="text-3xl font-semibold text-[#01545D] mb-3">
              We couldn't open your check-in
            </div>
            <div role="alert" className="text-base text-[#1C1C1C]">
              {error}
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center">
            <div className="text-4xl font-semibold text-[#01545D] text-center mb-3">
              Opening Check-In
            </div>
            <div className="mt-4">
              <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-[#01545D]"></div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default CheckinShortLink;
